/**
 * voice-v8 — „Mein Alltag"-API-Helfer (Task 5, pre.988).
 *
 * Ruft GET /api/alltag auf und gibt die strukturierten Tages-Daten zurück.
 */

export interface AlltagSection {
  heading: string;
  text: string;
}

export interface AlltagEntry {
  id: string;
  title: string;
  summary: string;
  todos: string[];
  /** 2026-07-18: nur die ID — der Text wird beim Aufklappen nachgeladen. */
  transcriptFileId: string | null;
  audioUrl: string | null;
  /** Erzeugungszeitpunkt des Eintrags (Note, nach der Verarbeitung) — ISO. */
  createdAt: string;
  /** Thematische Abschnitte (Schritt 2) — leer, wenn der Digest keine lieferte. */
  sections: AlltagSection[];
  /** Stimmung des Eintrags (1 Wort aus dem Digest) — null wenn keine. */
  mood?: string | null;
  /** 2026-07-12 (Martin): Ort der Aufnahme — null wenn kein GPS-Fix. */
  location?: { lat: number; lon: number; place: string | null } | null;
  /** 2026-07-13 (Martin, Reformation): exakte Start-Zeit der Aufnahme (ISO) —
   *  aus dem recordedAt-Marker; null bei Altdaten. Fuer die Zeitachse. */
  recordedAt?: string | null;
  /** 2026-07-13 (Martin, Reformation): Endzeit des Sprech-Blocks (ISO) —
   *  Basis fuer die Still-Luecken zwischen Aufnahmen. null wenn unbekannt. */
  recordedUntil?: string | null;
}

export interface AlltagDay {
  dayKey: string; // YYYY-MM-DD (Europe/Vienna)
  rollup: string | null;
  entries: AlltagEntry[];
}

/** Eine Aufnahme, die noch verarbeitet wird (kein fertiger Eintrag). */
interface AlltagProcessing {
  id: string;
  title: string;
  createdAt: string;
}

/**
 * 2026-07-18 (Martin): Eine Aufnahme, deren Verarbeitung AUSGEFALLEN ist —
 * aelter als 30 min, kein Eintrag, aber ein Transkript mit echtem Inhalt.
 * Frueher komplett unsichtbar; jetzt als eigene Karte gemeldet.
 */
interface AlltagUnprocessed {
  id: string;
  title: string;
  createdAt: string;
  /** Buchstaben im Roh-Transkript (>= 15) — Beleg, dass Sprache vorliegt. */
  transcriptChars: number;
}

interface AlltagResponse {
  ok: boolean;
  days: AlltagDay[];
  /** Aufnahmen in Verarbeitung (transkribiert/verdichtet) — optional. */
  processing?: AlltagProcessing[];
  /** 2026-07-18: Aufnahmen mit Transkript, aber ohne Zusammenfassung. */
  unprocessed?: AlltagUnprocessed[];
  /** ID des „Mein Alltag"-Projekts — für den Kapitel-Player (/narrate). */
  projectId?: string;
  /**
   * 2026-08-07 (Ticket 1631): WHOOP-Werte je Tag, Schluessel "YYYY-MM-DD".
   * Additiv — fehlt das Feld (aelterer Server, WHOOP nicht verbunden),
   * bleibt die Ansicht wie bisher.
   */
  vitalwerte?: Record<
    string,
    { erholung?: number; hrv?: number; ruhepuls?: number; belastung?: number }
  >;
}

/**
 * 2026-07-18 (Martin, Verschwendung): schlanke Sofort-Sicht auf „Mein Alltag".
 * WARUM: Widget, Navi-Puls und der Beobachtungs-Poll brauchten je EIN Feld,
 * holten dafuer aber die komplette Liste (~120 kB, ~180 Notiz-Dateien).
 */
export interface AlltagNow {
  /** Aufnahmen in Verarbeitung (juenger als 30 min, noch ohne Eintrag). */
  processing: number;
  /** Titel des juengsten Eintrags („Alltag DD.MM.YYYY, HH:MM") — null wenn keiner. */
  lastEntryTitle: string | null;
  /** Anlage-Zeit des juengsten Eintrags (ISO) — Aenderungs-Signal fuer den Poll. */
  lastEntryAt: string | null;
  /** Anzahl der Eintraege von HEUTE (Europe/Vienna). */
  todayEntries: number;
}

/** Verarbeitungs-Status eines Tages (Aufnahmen vs. Auswertungen/Essenz). */
export interface AlltagStatusDay {
  date: string;
  recordings: number;
  digests: number;
  transcripts: number;
  essence: boolean;
  complete: boolean;
}


/**
 * Badge-Text fuer einen unvollstaendigen Alltag-Tag.
 * Fehlende Essenz bleibt still — wie Geist/Seele in 2235: Zeile weg,
 * kein „Essenz fehlt“, nichts erfinden. Nur echte Luecken bei der
 * Auswertung der Aufnahmen.
 */
export function dayStatusTeile(
  st: Pick<AlltagStatusDay, "recordings" | "digests" | "complete">,
): string[] {
  if (st.complete) return [];
  const parts: string[] = [];
  const missing = st.recordings - st.digests;
  if (missing > 0)
    parts.push(missing === 1 ? "1 ohne Auswertung" : `${missing} ohne Auswertung`);
  return parts;
}

interface AlltagStatusResponse {
  ok: boolean;
  days?: AlltagStatusDay[];
  /** Fehlt bei aelteren Servern — Aufrufer muessen damit rechnen. */
  now?: AlltagNow;
}

/**
 * GET /api/alltag/status — billig (Datei-Liste aus der DB, kein Lesen der
 * Notizen). Wirft NIE: liefert bei Fehler/Offline null, damit Aufrufer wie der
 * Navi-Puls kein try/catch brauchen.
 */
export async function fetchAlltagStatus(
  days = 1,
): Promise<AlltagStatusResponse | null> {
  try {
    const res = await fetch(`/api/alltag/status?days=${days}`, {
      credentials: "same-origin",
    });
    if (!res.ok) return null;
    return (await res.json()) as AlltagStatusResponse;
  } catch {
    return null;
  }
}

export async function fetchAlltag(): Promise<AlltagResponse> {
  const res = await fetch("/api/alltag", { credentials: "same-origin" });
  if (!res.ok) {
    throw new Error(`GET /api/alltag ${res.status}: ${res.statusText}`);
  }
  return (await res.json()) as AlltagResponse;
}

// --- Orte / meistbesuchte Plaetze (Phase E, 2026-07-13, Martin) ------------

export interface PlaceCluster {
  id: string;
  label: string;
  lat: number;
  lon: number;
  /** Summe der geclampten Sprech-Dauern (Untergrenze — nur mit Endzeit). */
  totalMinutes: number;
  visitCount: number;
  sampleCount: number;
  dayCount: number;
  firstVisit: string | null;
  lastVisit: string | null;
}

export interface PlacesMeta {
  totalEntries: number;
  withLocation: number;
  withoutLocation: number;
  withDuration: number;
  clusteredMinutes: number;
  radiusMeters: number;
}

interface PlacesResponse {
  ok: boolean;
  places: PlaceCluster[];
  /** null, wenn (noch) kein Alltag-Projekt existiert. */
  meta: PlacesMeta | null;
}

/** Rangliste der meistbesuchten Orte (nach Verweildauer). */
export async function fetchAlltagPlaces(): Promise<PlacesResponse> {
  const res = await fetch("/api/alltag/places", { credentials: "same-origin" });
  if (!res.ok) {
    throw new Error(`GET /api/alltag/places ${res.status}: ${res.statusText}`);
  }
  return (await res.json()) as PlacesResponse;
}

// --- Sprecher-Register verwalten (Phase D, 2026-07-13, Martin) -------------

export interface Speaker {
  name: string;
  createdAt: string;
  /** true fuer "Du" (Martins eigener Stimmabdruck) — geschuetzt. */
  isMe: boolean;
}

interface SpeakersResponse {
  ok: boolean;
  speakers: Speaker[];
}

/** Bekannte Stimmen (Register) laden. */
export async function fetchSpeakers(): Promise<SpeakersResponse> {
  const res = await fetch("/api/speakers", { credentials: "same-origin" });
  if (!res.ok) throw new Error(`GET /api/speakers ${res.status}`);
  return (await res.json()) as SpeakersResponse;
}

/** Einen bekannten Sprecher umbenennen. */
export async function renameSpeaker(
  from: string,
  to: string,
): Promise<SpeakersResponse> {
  const res = await fetch("/api/speakers/rename", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ from, to }),
  });
  if (!res.ok) throw new Error(`POST /api/speakers/rename ${res.status}`);
  return (await res.json()) as SpeakersResponse;
}

/** Einen bekannten Sprecher (Stimmabdruck) entfernen. */
export async function deleteSpeaker(name: string): Promise<SpeakersResponse> {
  const res = await fetch("/api/speakers/delete", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error(`POST /api/speakers/delete ${res.status}`);
  return (await res.json()) as SpeakersResponse;
}

/** Einen Alltag-Eintrag löschen (Notiz + Aufnahme + Transkript). */
export async function deleteAlltagEntry(id: string): Promise<void> {
  const res = await fetch(`/api/alltag/${encodeURIComponent(id)}`, {
    method: "DELETE",
    credentials: "same-origin",
  });
  if (!res.ok) {
    throw new Error(`DELETE /api/alltag ${res.status}: ${res.statusText}`);
  }
}

// ── Sprecher-Erkennung (Schritt 3) ──────────────────────────────────────────
interface SpeakerTurn {
  speaker: string;
  text: string;
}
export interface SpeakerInfo {
  label: string;
  seconds: number;
  similarity: number;
  isMe: boolean;
}
export interface SpeakerData {
  ok: boolean;
  status: "none" | "running" | "done" | "error";
  result?: { me: string; speakers: SpeakerInfo[]; turns: SpeakerTurn[] } | null;
  error?: string | null;
}

/** Status + Ergebnis der Sprecher-Erkennung einer Aufnahme abrufen. */
export async function getSpeakerId(entryId: string): Promise<SpeakerData> {
  const res = await fetch(
    `/api/alltag/${encodeURIComponent(entryId)}/speakers`,
    { credentials: "same-origin" },
  );
  if (!res.ok) throw new Error(`GET speakers ${res.status}`);
  return (await res.json()) as SpeakerData;
}

/** Sprecher-Erkennung für eine Aufnahme starten (asynchron, CPU-intensiv). */
export async function startSpeakerId(
  entryId: string,
): Promise<{ status: string }> {
  const res = await fetch(
    `/api/alltag/${encodeURIComponent(entryId)}/speakers`,
    { method: "POST", credentials: "same-origin" },
  );
  if (!res.ok) throw new Error(`POST speakers ${res.status}`);
  return (await res.json()) as { status: string };
}

/**
 * Einen erkannten Sprecher benennen (z. B. „Sprecher 2" → „Mama"). Speichert
 * Name + Stimmabdruck im Register (Wiedererkennung) und liefert das umbeschrif-
 * tete Ergebnis zurück.
 */
export async function nameSpeaker(
  entryId: string,
  label: string,
  name: string,
): Promise<SpeakerData> {
  const res = await fetch(
    `/api/alltag/${encodeURIComponent(entryId)}/speakers/name`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ label, name }),
    },
  );
  if (!res.ok) throw new Error(`POST speakers/name ${res.status}`);
  return (await res.json()) as SpeakerData;
}


/** Eine Stimme, die noch niemand benannt hat — ueber Aufnahmen zusammengelegt. */
export interface OffeneStimmeZeile {
  sekunden: number;
  vorkommen: number;
  tage: number;
  zuletzt: string;
  besteAufnahme: { aufnahmeId: string; label: string; sekunden: number };
  /** Nach genau dieser Stimme hat Vespera in der Glocke gefragt, und die Frage ist offen — sie steht oben. */
  frage?: boolean;
}

/**
 * Die Stimmen holen, die noch niemand benannt hat (Ticket 2391).
 *
 * `frage`: der Schlüssel der Glocken-Frage („stimme-0cc81939“), den „Benennen“ mitgibt — der Server stellt diese Stimme
 * nach oben, auch wenn sie nicht zu den zuletzt gehörten zählt.
 *
 * ⚠️ Wird nur auf Anforderung geladen. Es ist eine Liste zum Aufmachen,
 *    kein Hinweis, der sich meldet — die Glocke dafuer hat Martin am
 *    24.07.2026 ausdruecklich abbestellt.
 */
export async function fetchOffeneStimmen(frage?: string | null): Promise<{
  stimmen: OffeneStimmeZeile[];
  gepruefteAufnahmen: number;
  stimmenGesamt: number;
}> {
  const url = frage ? `/api/alltag/stimmen-offen?frage=${encodeURIComponent(frage)}` : "/api/alltag/stimmen-offen";
  const res = await fetch(url, {
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`GET stimmen-offen ${res.status}`);
  const d = (await res.json()) as {
    stimmen?: OffeneStimmeZeile[];
    gepruefteAufnahmen?: number;
    stimmenGesamt?: number;
  };
  return {
    stimmen: d.stimmen ?? [],
    gepruefteAufnahmen: d.gepruefteAufnahmen ?? 0,
    stimmenGesamt: d.stimmenGesamt ?? 0,
  };
}

/**
 * Die Tageszusammenfassung eines Tages neu generieren (ersetzt den alten Text).
 * Gibt den frischen Markdown-Roll-up zurück.
 */
export async function regenerateAlltagRollup(dayKey: string): Promise<string> {
  const res = await fetch("/api/alltag/rollup", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ dayKey }),
  });
  if (!res.ok) {
    throw new Error(`POST /api/alltag/rollup ${res.status}: ${res.statusText}`);
  }
  const data = (await res.json()) as { ok: boolean; markdown?: string };
  return data.markdown ?? "";
}
